import type { DayEvent } from './CalendarGrid';

type Props = {
  date: string | null;
  events: DayEvent[];
  onClose: () => void;
};

const MONTHS_TR = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];

const STEP_BG: Record<string, string> = {
  teal: 'bg-teal-50 text-teal-700 border-teal-200',
  cyan: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
  orange: 'bg-orange-50 text-orange-700 border-orange-200',
  green: 'bg-green-50 text-green-700 border-green-200',
};

function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  return `${d.getDate()} ${MONTHS_TR[d.getMonth()]} ${d.getFullYear()}`;
}

function formatOpDate(dateStr: string) {
  const d = new Date(dateStr);
  return `${d.getDate()} ${MONTHS_TR[d.getMonth()]} ${d.getFullYear()}`;
}

type PatientGroup = {
  patientName: string;
  clinic: string;
  steps: DayEvent[];
};

export default function DayDetailPanel({ date, events, onClose }: Props) {
  if (!date) {
    return (
      <div className="bg-white rounded-2xl p-8 flex flex-col items-center justify-center text-center h-full min-h-[400px]">
        <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mb-4">
          <i className="ri-calendar-line text-3xl text-teal-400"></i>
        </div>
        <h3 className="text-base font-semibold text-gray-700 mb-1">Gün Seçin</h3>
        <p className="text-sm text-gray-400">Takvimden bir güne tıklayarak o güne ait takip listesini görüntüleyin.</p>
      </div>
    );
  }

  // Group by patient
  const groupMap = new Map<string, PatientGroup>();
  for (const ev of events) {
    const key = ev.patientName;
    if (!groupMap.has(key)) {
      groupMap.set(key, { patientName: ev.patientName, clinic: ev.clinic ?? '', steps: [] });
    }
    groupMap.get(key)!.steps.push(ev);
  }
  const groups = Array.from(groupMap.values());

  const activeEvents = events.filter((e) => !e.completed);
  const completedEvents = events.filter((e) => e.completed);

  return (
    <div className="bg-white rounded-2xl overflow-hidden flex flex-col">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-gray-900">{formatDate(date)}</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            {events.length === 0
              ? 'Takip yok'
              : `${activeEvents.length} aktif · ${completedEvents.length} tamamlandı`}
          </p>
        </div>
        <button
          onClick={onClose}
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-gray-400"
        >
          <i className="ri-close-line text-lg"></i>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-5">
        {events.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="ri-calendar-check-line text-2xl text-gray-400"></i>
            </div>
            <p className="text-sm text-gray-500">Bu güne ait randevu veya takip yok.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Active steps first */}
            {groups.map((group) => {
              const groupActive = group.steps.filter((s) => !s.completed);
              const groupDone = group.steps.filter((s) => s.completed);

              return (
                <div key={group.patientName} className="border border-gray-100 rounded-xl overflow-hidden">
                  {/* Patient header */}
                  <div className="px-4 py-3 bg-gray-50 flex items-center gap-3">
                    <div className="w-9 h-9 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                      {group.patientName.slice(0, 1).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate">{group.patientName}</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        {group.clinic && (
                          <span className="text-xs text-teal-600 flex items-center gap-1">
                            <i className="ri-hospital-line text-xs"></i>{group.clinic}
                          </span>
                        )}
                        {group.steps[0]?.operationDate && (
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <i className="ri-scissors-cut-line text-xs"></i>
                            {formatOpDate(group.steps[0].operationDate)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      {groupActive.length > 0 && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-bold">
                          {groupActive.length} bekliyor
                        </span>
                      )}
                      {groupDone.length > 0 && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-600 rounded-full text-xs font-bold">
                          {groupDone.length} tamam
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Steps */}
                  <div className="divide-y divide-gray-50">
                    {group.steps.map((ev, i) => (
                      <div key={i} className={`px-4 py-3 flex items-center gap-3 ${ev.completed ? 'opacity-60' : ''}`}>
                        <div className={`w-8 h-8 flex items-center justify-center rounded-lg border ${STEP_BG[ev.stepColor] ?? 'bg-gray-50 text-gray-600 border-gray-200'} flex-shrink-0`}>
                          <i className={`${ev.stepIcon} text-sm`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${ev.completed ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                            {ev.stepLabel}
                          </p>
                        </div>
                        {ev.completed ? (
                          <span className="flex items-center gap-1 text-xs font-medium text-green-600 flex-shrink-0">
                            <i className="ri-check-double-line"></i>Tamam
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs font-medium text-amber-600 flex-shrink-0">
                            <i className="ri-time-line"></i>Bekliyor
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
